
import React, { useState, useEffect } from "react";
import {
    Flex,
    Icon,
    Image,
    Text,
    Link,
    Button,
    IconButton,
    Tooltip,
    useClipboard,
    Box,
    HStack,
    VStack,
    Badge,
    Popover, PopoverTrigger, PopoverContent, PopoverArrow, PopoverBody
} from "@chakra-ui/react";
// Custom components
import Card from "components/card/Card.js";
import RelevanceBadge from "components/badge/RelevanceBadge";
// Assets
import { StarIcon } from "@chakra-ui/icons";
import { MdCheckCircle, MdContentCopy, MdStore,MdCalendarToday } from "react-icons/md";
import { IoMdAlert } from "react-icons/io";

import Amazon from 'assets/img/amazon.ico';
import Flipkart from 'assets/img/flipkart.ico';
import Snapdeal from 'assets/img/snapdeal.ico';
import Meesho from 'assets/img/meesho.ico';
import Glowroad from 'assets/img/glowroad.ico';
import Shopsy from 'assets/img/shopsy.ico';
import freeup from 'assets/img/freeup.ico';
import CommentPopover from "./CommentPopover";

// --- Internal Helper Components ---

// 1. Overlay (Grid View Only)
const StatusOverlay = ({ active }) => {
    if (!active) return null;
    return (
        <Box
            position="absolute"
            top="0" left="0" right="0" bottom="0"
            bg="blackAlpha.700"
            borderRadius="xl"
            zIndex="2"
            display="flex"
            flexDirection="column"
            alignItems="center"
            justifyContent="center"
            transition="all 0.2s"
        >
            <Icon as={MdCheckCircle} color="green.400" w={10} h={10} mb={2} />
            <Text color="white" fontWeight="bold" fontSize="xs" letterSpacing="wider">
                WHITELISTED
            </Text>
        </Box>
    );
};

// 2. Action Buttons (Whitelist Only)
const ActionButtons = ({ isAdmin, active, onToggle, layout }) => {
    if (!isAdmin) return null;

    return (
        <HStack spacing={2} width={layout === 'grid' ? "100%" : "auto"} justify={layout === 'grid' ? "center" : "flex-end"}>
            <Tooltip label='Whitelist Seller'>
                <Button
                    size="md"
                    flex={layout === 'grid' ? 1 : 'unset'}
                    leftIcon={<Icon as={MdCheckCircle} />}
                    colorScheme={active ? "green" : "gray"}
                    variant="solid"
                    onClick={onToggle}
                    minW={layout === 'list' ? "110px" : "auto"}
                >
                    Whitelist
                </Button>
            </Tooltip>
        </HStack>
    );
};

// --- Formats and displays the detection date; click the calendar icon to reveal

const formatDate = (d) => {
  if (!d) return null;
  const parsed = new Date(d);
  if (isNaN(parsed.getTime())) return d; // fall back to raw value if unparsable
  return parsed.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
};
const DetectedDateBadge = ({ date, dateReported, dateTakendown, variant = "grid" }) => {
  if (!date) return null;

  const extraRows = [
    dateReported ? { label: "Reported", value: formatDate(dateReported) } : null,
    dateTakendown ? { label: "Takedown", value: formatDate(dateTakendown) } : null,
  ].filter(Boolean);

  const popoverContent = (
    <PopoverContent w="auto" minW="150px">
      <PopoverArrow />
      <PopoverBody py={2} px={3}>
        <VStack align="start" spacing={1}>
          {/* <Text fontSize="xs" color="gray.500">Detected: <b>{formatDate(date)}</b></Text> */}
          {extraRows.map((row) => (
            <Text key={row.label} fontSize="xs" color="gray.500">{row.label}: <b>{row.value}</b></Text>
          ))}
        </VStack>
      </PopoverBody>
    </PopoverContent>
  );

  if (variant === "list") {
    return (
      <Popover trigger="click" placement="top" isLazy>
        <HStack spacing={1} p="1">
          <PopoverTrigger>
            <IconButton
              aria-label="Show status dates"
              icon={<Icon as={MdCalendarToday} color="gray.400" boxSize="11px" />}
              size="xs"
              variant="ghost"
              minW="auto"
              h="auto"
            //   p="1"
            />
          </PopoverTrigger>
          <Text>Detected: <Text as="span" fontWeight="semibold" color="inherit">{formatDate(date)}</Text></Text>
        </HStack>
        {popoverContent}
      </Popover>
    );
  }

  return (
    <Popover trigger="click" placement="top" isLazy>
      <PopoverTrigger>
        <Badge
          as="button"
          type="button"
          colorScheme="purple"
          variant="subtle"
          fontSize="9px"
          px={2}
          py={1}
          borderRadius="md"
          display="flex"
          alignItems="center"
          gap="4px"
          w="fit-content"
          cursor="pointer"
        >
          <Icon as={MdCalendarToday} boxSize="10px" />
          Detected: {formatDate(date)}
        </Badge>
      </PopoverTrigger>
      {popoverContent}
    </Popover>
  );
};

// --- GridView Design ---
const GridView = ({ product, index, isAdmin, toggleWhiteList, handleToggleWhiteList, common }) => {
    const { getPlatformIcon, renderStars, onCopy, toolTipMessage, chooseAbuseType } = common;

    return (
        <Card
            p='5px'
            bg="white" _dark={{ bg: "navy.800" }}
            transition='0.3s cubic-bezier(.25,.8,.25,1)'
            _hover={{ shadow: "lg", transform: "translateY(-4px)" }}
            border={toggleWhiteList ? "2px solid" : "1px solid"}
            borderColor={toggleWhiteList ? "green.400" : "transparent"}
            overflow='hidden' h="100%" display="flex" flexDirection="column" position="relative"
        >
            {/* Image Section */}
            <Box position='relative' bg='white' p='15px' h='200px' borderBottom="1px solid" borderColor="gray.100" borderRadius="xl" overflow="hidden">
                <StatusOverlay active={toggleWhiteList} />
                <Box position="absolute" top="8px" right="8px" zIndex={4}>
                    <CommentPopover product={product} isAdmin={isAdmin} onSave={common.onSaveComments} size="sm" compact />
                </Box>
                
                <Link href={product.image_link} isExternal w='100%' h='100%' display="block">
                    <Image src={product.image_link} w='100%' h='100%' objectFit='contain' />
                </Link>

                <Box position='absolute' top='10px' left='10px' bg="white" borderRadius="md" p="1" shadow="sm" border="1px solid" borderColor="gray.100">
                    <Image src={getPlatformIcon(product.platform_id)} w='18px' h='18px' />
                </Box>
                <Flex position='absolute' top='10px' right='10px' alignItems={"center"}> 
                {product.global_status && (
                    //  <Box position='absolute' top='10px' right='10px'>
                        <Tooltip label={toolTipMessage(product.global_status)}>
                            <Box bg="white" borderRadius="full" p={1} shadow="sm">
                                <Icon 
                                    as={(product.global_status === 20 || product.global_status === 21) ? IoMdAlert : MdCheckCircle} 
                                    color={(product.global_status === 20 || product.global_status === 21) ? "red.500" : "green.500"} 
                                    w={5} h={5}
                                />
                            </Box>
                        </Tooltip>
                    //  </Box>
                )}

                {product.abuse_type_id && (
                        <Badge colorScheme="red" variant="subtle" fontSize="0.7em">
                            {chooseAbuseType()}
                        </Badge>
                )}
                </Flex>
            </Box>

            {/* Content Section */}
            <VStack px='15px' align='start' spacing='8px' flex='1' py={2}>
                <Link href={product.prod_link} isExternal w="100%">
                    <Text color="brands.900" _dark={{ color: "white" }} fontWeight='700' fontSize='sm' noOfLines={2} h="40px">
                        {index + 1}. {product.title}
                    </Text>
                </Link>
                        <HStack w="100%" justify="space-between">
                                             {!product.seller_name?.toLowerCase().startsWith("xxxx") && (
                                                 <HStack spacing="1px">
                                                 {renderStars(product.overall_rating)}
                                                 </HStack>
                                             )}
                     
                                 <Box ml="auto">
                                {product.relevance && <RelevanceBadge value={product.relevance} />}
                             </Box>
                         </HStack>

                <HStack w="100%" justify="space-between" align="center">
                    <Text hidden={product.seller_name?.toLowerCase().startsWith("xxxx")} color="brands.900" _dark={{ color: "white" }} fontWeight='900' fontSize='xl'>
                         {product.selling_price > 0 ? `₹${product.selling_price}` : "N/A"}
                    </Text>
                        <DetectedDateBadge date={product.date_detected} dateReported={product.date_report} dateTakendown={product.date_takendown} variant="grid" />

                </HStack>

              
                 {/* Gray Info Box */}
                <VStack w='100%' align="start" spacing={1} bg={"gray.50"} p="2" borderRadius="md">
                     {/* Abuse Type Display */}
                    {/* <HStack w="100%" justify="space-between">
                         <Text fontSize='13px' color="red.500" fontWeight="bold">
                            {chooseAbuseType(product.abuse_type_id)}
                        </Text>
                    </HStack> */}
                    <HStack w="100%" justify="space-between" pt={1} borderTop="1px dashed" borderColor="gray.200">
                        <Text fontSize='13px' color="gray.500">Code: <b>{product.prod_code}</b></Text>
                        <IconButton size='xs' icon={<MdContentCopy />} variant='ghost' h="20px" aria-label="Copy" onClick={onCopy} />
                    </HStack>

                    <HStack w="100%" justify="space-between">
                         <Text fontSize='13px' color="gray.500" noOfLines={1} hidden={product.seller_name?.toLowerCase().startsWith("xxxx")}>
                            Seller: <b>{product.seller_name}</b>
                        </Text>
                    </HStack>
                   
                   {product.store_name && (
                       <HStack fontSize='13px' color="gray.500" hidden={product.seller_name?.toLowerCase().startsWith("xxxx")}>
                           <Icon as={MdStore} />
                           <Text noOfLines={1}>{product.store_name}</Text>
                       </HStack>
                   )}
                   
                   {product.manufacturer && (
                       <Text fontSize='13px' color="gray.500" noOfLines={1}>
                           Mfg: {product.manufacturer}
                       </Text>
                   )}

                    
                </VStack>
            </VStack>

            {/* Admin Footer */}
            {isAdmin && (
                <Box p={2} borderTop="1px solid" borderColor="gray.100">
                    <ActionButtons isAdmin={isAdmin} active={toggleWhiteList} onToggle={handleToggleWhiteList} layout="grid" />
                </Box>
            )}
        </Card>
    );
};

// --- ListView Design ---
const ListView = ({ product, index, isAdmin, toggleWhiteList, handleToggleWhiteList, common }) => {
    const { getPlatformIcon, renderStars, onCopy, toolTipMessage, chooseAbuseType, hasCopied } = common;

    // List view specific styles
    const bgStyle = toggleWhiteList 
        ? { bg: "green.50", _dark: { bg: "rgba(72, 187, 120, 0.1)" } } 
        : { bg: "white", _dark: { bg: "navy.800" } };

    return (
        <Card
            p="16px"
            mb="10px"
            position="relative"
            overflow="hidden"
            borderLeftWidth={toggleWhiteList ? "5px" : "0px"}
            borderLeftColor={toggleWhiteList ? "green.400" : "transparent"}
            transition="all 0.2s"
            bg={bgStyle.bg}
            _dark={bgStyle._dark}
            boxShadow="sm"
            _hover={{ boxShadow: "md" }}
        >
            <Flex align="center" gap={4}>
                {/* 1. Image */}
                <Box position="relative" w="100px" h="100px" flexShrink={0}>
                    <Link href={product.image_link} isExternal w='100%' h='100%' display="block">
                        <Image src={product.image_link} w="100%" h="100%" objectFit="contain" />
                    </Link>
                    <Box 
                        position='absolute' bottom='0' left='0' 
                        bg="white" _dark={{ bg: "navy.900" }} 
                        borderRadius="tr-md" p="1" shadow="sm"
                    >
                        <Image src={getPlatformIcon(product.platform_id)} w='16px' />
                    </Box>
                </Box>

                {/* 2. Details */}
                <Box flex="1">
                    <Flex align="center" mb={1} wrap="wrap">
                        <Text fontWeight="bold" fontSize="md" color="brands.900" _dark={{ color: "white" }} mr={2}>
                            {index + 1}.&nbsp;
                            <Link href={product.prod_link} isExternal textDecoration="none" _hover={{ textDecoration: "underline" }}>
                                {product.title}
                            </Link>
                        </Text>
                        
                        {/* Whitelist Badge */}
                        {toggleWhiteList && <Badge colorScheme="green" variant="solid" mr={2}>WHITELISTED</Badge>}
                        
                        {/* Global Status Icon */}
                        {product.global_status && (
                             <Tooltip label={toolTipMessage(product.global_status)}>
                                 <Box as="span" display="flex" alignItems="center">
                                     <Icon 
                                        as={(product.global_status === 20 || product.global_status === 21) ? IoMdAlert : MdCheckCircle} 
                                        color={(product.global_status === 20 || product.global_status === 21) ? "red.500" : "green.500"} 
                                     />
                                 </Box>
                             </Tooltip>
                        )}
                    </Flex>
                    
                    <HStack spacing={4} mb={2}>
                        <Text fontSize="sm" color="secondaryGray.600" _dark={{ color: "gray.400" }}>
                            {product.keyword}
                        </Text>
                        <HStack spacing="1px" hidden={product.seller_name?.toLowerCase().startsWith("xxxx")}>{renderStars(product.overall_rating)}</HStack>
                         {/* Abuse Type Badge in List View */}
                         <Badge colorScheme="red" variant="subtle" fontSize="0.7em">
                            {chooseAbuseType()}
                        </Badge>
                        <DetectedDateBadge date={product.date_detected} dateReported={product.date_report} dateTakendown={product.date_takendown} variant="grid" />
                                {product.relevance && <RelevanceBadge value={product.relevance} />}
                            

                    </HStack>

                    <Flex gap={6} align="center" fontSize="sm" wrap="wrap">
                        {product.selling_price > 0 && (
                            <Text fontWeight="bold" color="brands.900" _dark={{ color: "white" }} hidden={product.seller_name?.toLowerCase().startsWith("xxxx")}>
                                ₹{product.selling_price}
                            </Text>
                        )}
                        {product.mrp > 0 && (
                            <Text color="secondaryGray.600" textDecoration="line-through" hidden={product.seller_name?.toLowerCase().startsWith("xxxx")}>
                                ₹{product.mrp}
                            </Text>
                        )}
                        
                        <HStack>
                             <Text color="secondaryGray.600">Code: {product.prod_code}</Text>
                             <Tooltip label={hasCopied ? "Copied!" : "Copy Code"}>
                                <IconButton 
                                    size='xs' 
                                    icon={<MdContentCopy />} 
                                    onClick={onCopy} 
                                    variant='ghost' 
                                    aria-label="copy"
                                    colorScheme={hasCopied ? "green" : "gray"}
                                />
                             </Tooltip>
                        </HStack>
                        
                        {product.seller_name && (
                            <Text color="secondaryGray.600" hidden={product.seller_name?.toLowerCase().startsWith("xxxx")}>
                                Seller: <Text as="span" fontWeight="semibold" color="brands.900" _dark={{ color: "white" }}>{product.seller_name}</Text>
                            </Text>
                        )}

                         {product.manufacturer && (
                            <Text color="secondaryGray.600" >
                                Mfg: <Text as="span" fontWeight="semibold" color="brands.900" _dark={{ color: "white" }}>{product.manufacturer}</Text>
                            </Text>
                        )}
                    </Flex>
                </Box>

                {/* 3. Actions */}
                {isAdmin && (
                    <Flex direction="column" gap={2} align="flex-end">
                        <ActionButtons isAdmin={isAdmin} active={toggleWhiteList} onToggle={handleToggleWhiteList} layout="list" />
                    </Flex>
                )}
            </Flex>
        </Card>
    );
};


export default function TakedownItem(props) {
    const { product, handleUpdateSeller, removeFromUpdate, statusUpdate, setStatusUpdate, index, isAdmin, viewMode = "grid", abuseTypes } = props;
    
    // State
    const { hasCopied, onCopy } = useClipboard(product.prod_code);
    const [toggleWhiteList, setToggleWhiteList] = useState(false);
    const [localComments, setLocalComments] = useState(product.comments || []);
    // const [currentAbuseId]

    // Handlers
    const handleToggleWhiteList = async () => {
        setToggleWhiteList(!toggleWhiteList);
        if (!toggleWhiteList) {
            await handleUpdateSeller(product, 'W');
        } else {
            await removeFromUpdate(product, 'W');
        }
    };

    // Reset state
    useEffect(() => {
        setToggleWhiteList(false);
        setStatusUpdate(false);
    }, [statusUpdate]);

    useEffect(() => {
        setLocalComments(product.comments || []);
    }, [product.comments, product.pd_id]);

    // Helpers
    const getPlatformIcon = (platformId) => {
        switch (platformId) {
            case 1: return Amazon;
            case 2: return Flipkart;
            case 3: return Snapdeal;
            case 4: return Meesho;
            case 5: return Glowroad;
            case 6: return Shopsy;
            case 7: return freeup;
            default: return null;
        }
    };

    const renderStars = (rating) => {
        const val = rating === -1 ? 0 : rating;
        return Array.from({ length: 5 }, (_, i) => (
            <Icon as={StarIcon} key={i} color={i < Math.floor(val) ? "gold" : "gray.300"} />
        ));
    };

    const chooseAbuseType= (id) => {
      // Handle explicit "None" cases
      if (!product.abuse_type_id || product.abuse_type_id === 0) return 'None';

      // Find the abuse type object in the provided array
      const found = abuseTypes?.find(type => type.id === product.abuse_type_id);

      // Return label or fallback
      return found ? found.abuse_type : 'Choose Type';
    }

    const toolTipMessage = (status) => {
        if (status === 10) return "Tested and verified seller";
        if (status === 11) return "Official Seller";
        if (status === 20) return "Counterfeit products";
        if (status === 21) return "Blacklisted Seller";
        return "No info";
    };

    const handleSaveComments = async (comments) => {
        const backendUrl = process.env.REACT_APP_BACKEND_URL;
        const response = await fetch(`${backendUrl}/api/products/updateComments`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ products: [{ pd_id: product.pd_id, comments }] }),
        });

        if (!response.ok) {
            throw new Error("Failed to save comments");
        }

        setLocalComments(comments);
    };

    const commonProps = {
        getPlatformIcon,
        renderStars,
        onCopy,
        hasCopied,
        toolTipMessage,
        chooseAbuseType,
        onSaveComments: handleSaveComments
    };

    const cardProduct = { ...product, comments: localComments };

    return viewMode === 'grid'
        ? <GridView {...props} product={cardProduct} toggleWhiteList={toggleWhiteList} handleToggleWhiteList={handleToggleWhiteList} common={commonProps} />
        : <ListView {...props} product={cardProduct} toggleWhiteList={toggleWhiteList} handleToggleWhiteList={handleToggleWhiteList} common={commonProps} />;
}